import React from "react";
import type { PreviousDueItem } from "@/services/types";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { Badge } from "@/components/base/Badge";
import { formatDate } from "@/utils/format";

interface PreviousDuesAlertCardProps {
  memberName: string;
  groupName?: string;
  previousDues: PreviousDueItem[];
  totalDues?: number;
  className?: string;
  compact?: boolean;
}

export function PreviousDuesAlertCard({
  memberName,
  groupName,
  previousDues,
  totalDues,
  className = "",
  compact = false,
}: PreviousDuesAlertCardProps) {
  if (!previousDues || previousDues.length === 0) return null;

  const total =
    totalDues ??
    previousDues.reduce((sum, d) => sum + Number(d.balance || 0), 0);

  if (total <= 0) return null;

  return (
    <div
      className={`rounded-xl border border-amber-300 bg-amber-50/90 p-3.5 shadow-sm transition-all duration-200 hover:shadow-md ${className}`}
      role="alert"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-amber-500 text-white shadow-sm ring-4 ring-amber-100">
          <i className="ri-error-warning-fill text-lg" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <h4 className="text-xs font-bold uppercase tracking-wider text-amber-950">
                Previous Cycle Dues Alert
              </h4>
              <Badge color="warning" size="sm" className="font-semibold">
                {previousDues.length} Cycle{previousDues.length === 1 ? "" : "s"} Pending
              </Badge>
            </div>

            <div className="flex items-center gap-1 text-xs">
              <span className="text-amber-800 font-medium">Total Unpaid:</span>
              <span className="font-bold text-danger-700 text-sm">
                <CurrencyDisplay amount={total} />
              </span>
            </div>
          </div>

          <p className="mt-1 text-xs text-amber-900 leading-relaxed">
            <strong className="font-semibold text-amber-950">{memberName}</strong> has unpaid installment dues from previous cycles in {groupName ? <strong className="font-semibold text-amber-950">{groupName}</strong> : "this group"}. Please review or collect prior dues.
          </p>

          {!compact && (
            <div className="mt-2.5 space-y-1.5 rounded-lg border border-amber-200/80 bg-white/80 p-2.5">
              <div className="text-[11px] font-semibold text-amber-900 uppercase tracking-wide px-1">
                Pending Cycles Breakdown:
              </div>
              <div className="divide-y divide-amber-100">
                {previousDues.map((due) => {
                  const bal = Number(due.balance || 0);
                  const isOverdue = due.status === "OVERDUE";
                  return (
                    <div
                      key={due.installmentId || `${due.cycleNo}-${due.cycleId}`}
                      className="flex flex-wrap items-center justify-between gap-2 py-1.5 px-1 text-xs"
                    >
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center font-bold text-amber-950 bg-amber-100/80 px-2 py-0.5 rounded text-[11px]">
                          Cycle {due.cycleNo}
                        </span>
                        <span className="text-foreground-600">
                          {due.periodLabel || "Cycle installment"}
                        </span>
                        {due.dueDate ? (
                          <span className="text-[11px] text-foreground-400">
                            (Due: {formatDate(due.dueDate)})
                          </span>
                        ) : null}
                      </div>

                      <div className="flex items-center gap-2">
                        {Number(due.lateFee || 0) > 0 ? (
                          <span className="text-[10px] text-amber-700 font-medium bg-amber-50 border border-amber-200 px-1.5 py-0.2 rounded">
                            +₹{Number(due.lateFee).toLocaleString("en-IN")} Late Fee
                          </span>
                        ) : null}

                        <span
                          className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${
                            isOverdue
                              ? "bg-danger-100 text-danger-800"
                              : "bg-warning-100 text-warning-800"
                          }`}
                        >
                          {due.status}
                        </span>

                        <span className="font-bold text-danger-600 min-w-[70px] text-right">
                          <CurrencyDisplay amount={bal} />
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
